/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { hd, } from '@nx-fe/services';
import { IRemoteControlConnection, TransferOperation } from '../../remote-control-ng';
import { RemoteControlService } from './remote-control.service';
import { DeviceStateRegistryService } from './device-state-registry.service';
import { MemoryBuffer } from '../../remote-control-ng/utils/memoryBuffer';
import { ColorQuality, IInitRequestPacket, SignatureByte } from '../../remote-control-ng/definitions';
import { Session } from '../../remote-control-ng/session';
import { RestartThrottling } from '../../remote-control-ng/services/restartThrottling';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { BaseMessage } from 'app/modules/remote-control-ng/messages';
import { RemoteControlStreamingContext } from 'app/modules/remote-control-ng/context/deviceContext';
import { Injector } from '@angular/core';
import { StartRemoteControlMessage } from 'app/modules/remote-control-ng/messages/com/startRemoteControlMessage';

export class DeviceInfo {
  static readonly UNKNOWN = 0;
  static readonly CONNECTING = 1;
  static readonly CONNECTED = 2;
  static readonly HANDSHAKED = 3;
  static readonly ESTABLISHED = 4;
  static readonly CLOSED = 5;
  static readonly CONNECTION_ERROR = -1;
  static readonly INCOMPATIBLE = -2;
  static readonly REJECTED = -3;
  static readonly BUSY = -4;
  static readonly UNKNOWN_ERROR = -5;

  public deviceId: string;
  public skinName: string;
  public modelName: string;
  public status: number;
  public message: string;

  public constructor(deviceId: string, skinName: string) {
    this.deviceId = deviceId;
    this.skinName = skinName;
    this.status = DeviceInfo.CONNECTING;
  }
}

export class NgRemoteControlConnection implements IRemoteControlConnection {
  constructor(
    private rcModel: hd.IRemoteControlModel,
    private context: RemoteControlStreamingContext,
    private viaProxy: boolean,
    private remoteControlService: RemoteControlService,
    private deviceStateRegistry: DeviceStateRegistryService,
    private injector: Injector
  ) {
    this.restartThrottle = injector.get(RestartThrottling);
  }

  private restartThrottle: RestartThrottling;

  public onStatusUpdate: ((this: NgRemoteControlConnection, status: DeviceInfo) => void) | null;

  private webSocket: WebSocket;
  private deviceId: string;

  private onClose: (event: CloseEvent) => void;
  //private onError: (event: ErrorEvent) => void;

  connect = (startPoppedOut?: boolean): void => {
    // For now mcrc still needs to be initialized so that legacy JS code for file explorer and others can work. See mcrc.install()
    this.initMcrc(RcContext.instance);
    RcContext.instance.initialized = true;

    this.deviceId = this.rcModel.data['DeviceId'];

    const skin = this.rcModel.data['SkinName'];
    console.log(`Device id: ${this.deviceId}, Skin: ${skin}`);
    this.context.deviceInfo = new DeviceInfo(this.deviceId, skin);
    if (this.onStatusUpdate) {
      this.onStatusUpdate(this.context.deviceInfo);
    }

    if (this.viaProxy) {
      const uri = this.rcModel.uri;
      const proxy = 'wss://' + uri.replace('/wss/', '/proxy/');
      const hosts = this.rcModel.data['Host'];

      console.log(`Connecting to device ${this.deviceId} via proxy: ${proxy} - ${hosts}`);
      this.startConnectingProxy(proxy, hosts);
    } else {
      // The host names of DS returned from MS have port 5494 attached, remove it and replace with 443 for web socket connnections.
      const hosts = this.rcModel.data['Host'].split(',');
      const httpsPort = this.rcModel.data['HttpsPort'];
      hosts.forEach((item, index, array) => {
        const port = httpsPort && Number(httpsPort) !== 443 ? ':' + httpsPort : '';
        array[index] = item.split(':')[0] + port;
      });

      console.log(`Connecting to device ${this.deviceId} via hosts: ${hosts}`);
      this.startConnecting(hosts, 0);
    }
  };

  disconnect = () => {
    if (this.webSocket && this.webSocket.readyState !== WebSocket.CLOSED) {
      try {
        this.webSocket.close();
      } catch (error) {
        console.error('Error closing websocket:', error);
      }
    }

    this.context.$scale = 0;
  };

  isConnected = (): boolean => this.webSocket && this.webSocket.readyState === WebSocket.OPEN;

  getWebSocket = (): WebSocket => this.webSocket;

  send = (message: BaseMessage): void => {
    if (!this.isConnected()) {
      throw new Error(`WebSocket is not ready to send: ${this.webSocket.readyState}`);
    }

    this.webSocket.send(message.serialize());
  };

  // returns the device scale adjusted for the browserZoomLevel. The scale can be
  // returned with a similarly adjusted increment, for the zooming functionality
  getScale = (increment?: number): number => (this.context.$scale + (increment ?? 0)) / (devicePixelRatio ?? 1);

  getQuality = (): number => this.context.quality;

  getColorDepth = (): number => ColorQuality.ColorQuality32Bpp;

  startStreamingRC = (deviceId: string): void => this.remoteControlService.onRcStreamingStarted(deviceId);

  getVideoRecordingState = (): boolean => this.deviceStateRegistry.getCurrentState()?.recording;

  applyChange = (forceApply: boolean, scale?: number, quality?: number, colorDepth?: number): void => {
    this.restartThrottle.applyChange(forceApply, RcContext.instance, scale, quality, colorDepth);
  };

  public setupForPopout = (rcContext: RcContext): void => {
    // copy old mcrc to new mcrc
    this.initMcrc(rcContext);
    // comment out so that mcrc.transfer() will be called later from RemoteControlService.transferRemoteControlData()
    this.attachToSession(rcContext.session);

    RcContext.instance.security.initialize(this.context.permissions);
    rcContext.versionHi = RcContext.instance.versionHi;
    rcContext.versionLo = RcContext.instance.versionLo;
    rcContext.session.appUI.setOsType(this.context.osType);
    RcContext.instance.transfer(rcContext, rcContext.featureToggleService, TransferOperation.transfer);
  };

  public restoreFromPopout = (): void => {
    // nothing to do
    // RcContext.transfer(savedPartialRc);
  };

  public get scaleFactor(): number {
    return this.context.$scaleFactor;
  }

  public set scaleFactor(value: number) {
    this.context.$scaleFactor = value;
  }

  // TODO - add quality as initial parameter, need to update agent to support that
  private createStreamingWebInitRequest = (fps: number = 0, proxyHosts?: string): BaseMessage => {
    const scale = Math.floor(this.context.$scale * this.context.$scaleFactor);
    const keys = RcContext.instance.session.constructInitKeys(false, scale, undefined, undefined, fps, proxyHosts);

    const initRequestPacket: IInitRequestPacket = {
      deviceId: this.deviceId,
      userName: this.rcModel.data['UserName'],
      token: this.rcModel.data['RemoteControlSecurityToken'],
      sessionId: this.rcModel.data['SessionId'],
      keys: keys,
      host: '',
      versionHigh: Number(this.rcModel.data['AgentVersionHigh']),
      versionLow: Number(this.rcModel.data['AgentVersionLow']),
      sotiAssistDeviceId: '',
      forceOverride: RcContext.instance.forceOverride,
      disableUserDisplay: RcContext.instance.disableUserDisplay || false,
      disableUserInteraction: RcContext.instance.disableUserInteraction || false
    };
    return new StartRemoteControlMessage(initRequestPacket, SignatureByte.SignatureStreamingRCMessage);
  };


  private startConnecting = (hosts: string[], index: number): void => {
    const url = `wss://${hosts[index]}/MobiControl/rc`;
    this.webSocket = new WebSocket(url);
    this.webSocket.binaryType = 'arraybuffer';

    this.webSocket.onopen = (event: Event): void => {
      console.log(`Connected to ${hosts[index]}`);

      // When forceOverride is true, DS will link device to this connection when DS receives StartRemoteControlMsg from device (Stage = 2).
      // If $scale is not 0, agent will send a PC_FULLSCREEN immediately after sending StartRemoteControlMsg, in such case race condition could happen on DS
      // that PC_FULLSCREEN is processed before DS link device to this connection.
      // Set $scale 0 so that agent will wait for PC_RESTART before sending PC_FULLSCREEN.
      if (RcContext.instance.forceOverride) {
        this.context.$scale = 0;
      }

      // attach the websocket connection to mcrc
      this.attachToSession(RcContext.instance.session);
      this.send(this.createStreamingWebInitRequest());

      // if scale is pre-set (non-zero), we cannot send PC_RESTART until the first screen is received, otherwise remote-view popup might be dismissed by PC_RESTART.
      this.context.lastChangeTimestamp = this.context.$scale > 0 ? -1 : 0;
    };

    this.webSocket.onerror = (event: ErrorEvent): void => {
      console.error(`Failed connecting host ${hosts[index]}: ${JSON.stringify(event)}`);
      this.closeConnection();

      if (index + 1 < hosts.length) {
        this.startConnecting(hosts, index + 1);
      } else {
        console.error(`Failed connecting hosts ${hosts}`);
        if (this.onStatusUpdate) {
          this.context.deviceInfo.status = DeviceInfo.CONNECTION_ERROR;
          this.context.deviceInfo.message = `Couldn't connect to host: ${hosts}`;
          this.onStatusUpdate(this.context.deviceInfo);
        }
      }
    };

    this.webSocket.onclose = this.onWebSocketClosed;
  };

  private startConnectingProxy = (proxyUrl: string, hosts: string): void => {
    this.webSocket = new WebSocket(proxyUrl);
    this.webSocket.binaryType = 'arraybuffer';

    this.webSocket.onopen = (event: Event): void => {
      console.log(`Connected to proxy ${proxyUrl} - ${hosts}`);

      // When forceOverride is true, DS will link device to this connection when DS receives StartRemoteControlMsg from device (Stage = 2).
      // If $scale is not 0, agent will send a PC_FULLSCREEN immediately after sending StartRemoteControlMsg, in such case race condition could happen on DS
      // that PC_FULLSCREEN is processed before DS link device to this connection.
      // Set $scale 0 so that agent will wait for PC_RESTART before sending PC_FULLSCREEN.
      if (RcContext.instance.forceOverride) {
        this.context.$scale = 0;
      }

      this.send(this.createStreamingWebInitRequest( 0, hosts));

      // if scale is pre-set (non-zero), we cannot send PC_RESTART until the first screen is received, otherwise remote-view popup might be dismissed by PC_RESTART.
      this.context.lastChangeTimestamp = this.context.$scale > 0 ? -1 : 0;

      // attach the websocket connection to mcrc. This will detach the socket
      // from the NgRemoteControlConnection
      this.attachToSession(RcContext.instance.session);
    };

    this.webSocket.onerror = (event: ErrorEvent): void => {
      console.error(`Failed connecting proxy ${proxyUrl}: ${JSON.stringify(event)}`);
      this.closeConnection();

      console.error(`Failed connecting to proxy ${proxyUrl} - ${hosts}`);
      if (this.onStatusUpdate) {
        this.context.deviceInfo.status = DeviceInfo.CONNECTION_ERROR;
        this.context.deviceInfo.message = `Couldn't connect to host: ${hosts}`;
        this.onStatusUpdate(this.context.deviceInfo);
      }
    };

    this.webSocket.onclose = this.onWebSocketClosed;
  };

  private initMcrc = (rc: RcContext): void => {
    rc.clearStandardRc();
    rc.configure();
  };

  private attachToSession = (session: Session): void => {
    session.getConnection().attach(this);
    this.onClose = session.getSocket().onClose;
  };

  private closeConnection = (): void => {
    try {
      this.webSocket.close();
    } catch { }

    try {
      // close RC service connection
      RcContext.instance.session.close();
    } catch { }
  };

  private onWebSocketClosed = (event: CloseEvent): void => {
    console.log(`Connection closed: code: ${event.code}, reason: ${event.reason}`);
    this.closeConnection();

    // forward the event to mcrc, or redirect mcrc.dal.socketBridge.subscribeCloseHandler(callback) and call callback (RemoteControlService.webSocketCloseHandler())
    if (this.onClose) {
      this.onClose(event);
    } else {
      const sock = RcContext.instance.session.getSocket();
      if (sock) {
        sock.onClose(event);
      }
    }
  };

  public onRcSessionEstablished = (doc?: Document): void => {
    this.fireEvent('rcSessionEstablished', doc);
  };

  private fireEvent = (eventName: string, doc?: Document): void => {
    if (!doc) {
      doc = document;
    }

    const evt = doc.createEvent('HTMLEvents');
    evt.initEvent(eventName, true, true);
    const evtExt = evt as (Event & { deviceId: string });
    evtExt.deviceId = this.deviceId;
    doc.dispatchEvent(evtExt);
  };
}

export class LegacyRemoteControlConnection implements IRemoteControlConnection {
  constructor(private rcModel: hd.IRemoteControlModel) { }

  connect = (startPoppedOut?: boolean) => {
    RcContext.instance.setResponseData(this.rcModel.data);
    RcContext.instance.install(
      {
        provider: null, // use default
        host: this.rcModel.uri,
      },
      startPoppedOut
    );
  };

  disconnect = () => {
    RcContext.instance.clearStandardRc();
  };

  isConnected = (): boolean => RcContext.instance.session.isConnected();

  getWebSocket = (): WebSocket => {
    return undefined;
    // throw new Error('Method not implemented.');   // not being used yet
  };

  send = (message: MemoryBuffer): void => {
    //throw new Error('Method not implemented.');   // not being used yet
  };

  getScale = () => {
    return 1.0;
    // throw new Error('Method not implemented.');   // not being used yet
  };

  updateScale = (scale: number, browserZoomLevel?: number): boolean => {
    return true;
  };

  getQuality = (): number => {
    return 1.0;
    // throw new Error('Method not implemented.');   // not being used yet
  };

  getColorDepth = (): number => {
    return 24;
    // throw new Error('Method not implemented.');   // not being used yet
  };

  applyChange = (forceApply: boolean, scale?: number, quality?: number, colorDepth?: number): void => {
    // throw new Error('Method not implemented.');   // not being used yet
  };
}
